import { Stack } from "@mui/system";
import {
  Autocomplete,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";

import {
  setDefinitionModalSourceForCapturedByMultipleKeywords,
  setDefinitionModalTypeForCapturedByMultipleKeywords,
  setIsDefinitionModalOpenForCapturedByMultipleKeywords,
  setIsOpenOtherKeywordDialogForCapturedByMultipleKeywords,
  setOtherKeywordsForCapturedByMultipleKeywords,
  setSelectedRowsForCapturedByMultipleKeywords,
  setSelectedSearchTermForCapturedByMultipleKeywords,
} from "../../../../../../../../../features/searchTermTabSlice";
import { selectUserData } from "../../../../../../../../../features/userSlice";
import { CheckPermissions } from "../../../../../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import { useSearchParams } from "react-router-dom";
import DefinitionModalForCaptureByKeywords from "./DefinitionModalForCaptureByKeywords";
import OtherKeywordForCaptureByMultiple from "./OtherKeywordForCaptureByMultiple";
import CapturedMultipleKeywordFilterItems from "./CapturedMultipleKeywordFilterItems";

const icons = [
  {
    label: "Exclusive",
    src: "/Elock.svg",
    coloredSrc: "/EclockColored.svg",
    alt: "Exclusive lock",
    tooltip: "Set as Exclusive",
    dataTracker: "575",
  },
  {
    label: "Negative",
    src: "/Ncross.svg",
    coloredSrc: "/NcrossColored.svg",
    alt: "Negative cross",
    tooltip: "Set as Negative",
    dataTracker: "574",
  },
];

function CapturedMultipleKeywordFilterCard() {
  const dispatch = useDispatch();
  const response = useSelector(selectUserData);
  const [searchParams, setSearchParams] = useSearchParams();

  const hasCreateSearchTermTabPermission = CheckPermissions(
    response?.data?.products?.paid_traffic,
    "plan_campaign_ppc",
    "create_st_control",
  );

  const {
    capturedByMultipleKeywords,
    selectedSearchTermForCapturedByMultipleKeywords,
    selectedRowsForCapturedByMultipleKeywords,
    isDefinitionModalOpenForCapturedByMultipleKeywords,
    isOpenOtherKeywordDialogForCapturedByMultipleKeywords,
  } = useSelector((state: any) => state.searchTermTab);

  //--- Function to handle close of Other Keywords Dialog
  const handleCloseTheOtherKeywordDialog = () => {
    dispatch(setOtherKeywordsForCapturedByMultipleKeywords([])); // Clear other keywords data
    dispatch(setIsOpenOtherKeywordDialogForCapturedByMultipleKeywords(false)); // Close the modal
  };

  //-- Function to handle close of Save Changes Dialog
  const handleCloseDefinitionModal = () => {
    dispatch(setIsDefinitionModalOpenForCapturedByMultipleKeywords(false)); // Close the modal
    dispatch(setDefinitionModalSourceForCapturedByMultipleKeywords("")); // Clear source (headerIcon or saveButton)
  };

  const clearSelectedSearchTerm = () => {
    dispatch(setSelectedSearchTermForCapturedByMultipleKeywords(""));
  };

  const searchOptions = useMemo(() => {
    const set = new Set<string>();
    (capturedByMultipleKeywords || []).forEach((r: any) => {
      if (typeof r?.search_term === "string") set.add(r.search_term.trim());
    });
    return Array.from(set); // ["shoes", "hats", ...]
  }, [capturedByMultipleKeywords]);
  const handleClick = (type: string) => {
    dispatch(setIsDefinitionModalOpenForCapturedByMultipleKeywords(true));
    dispatch(
      setSelectedRowsForCapturedByMultipleKeywords(
        selectedRowsForCapturedByMultipleKeywords,
      ),
    );
    dispatch(
      setDefinitionModalSourceForCapturedByMultipleKeywords("headerIcon"),
    );
    dispatch(setDefinitionModalTypeForCapturedByMultipleKeywords(type));
  };

  const hasSelection = selectedRowsForCapturedByMultipleKeywords.length > 0;

  //-- this hold search params in url
  useEffect(() => {
    // Update selectedTemplate when searchParams change (e.g., on back/forward navigation)
    dispatch(
      setSelectedSearchTermForCapturedByMultipleKeywords(
        searchParams.get("search"),
      ),
    );
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Paper sx={{ padding: "8px 16px", marginTop: "8px", boxShadow: "none" }}>
        <Stack
          flexDirection={"row"}
          justifyContent={"start"}
          gap={2}
          // sx={{ padding: "16px 16px 0px 16px" }}
        >
          <Autocomplete
            fullWidth
            disablePortal
            freeSolo //user can type anything
            data-tracker="570"
            sx={{
              width: "390px",
              // ↓ Always show clear button when searchbox has value
              "& .MuiAutocomplete-clearIndicator": {
                visibility: selectedSearchTermForCapturedByMultipleKeywords
                  ? "visible"
                  : "hidden",
              },
            }}
            options={searchOptions}
            value={selectedSearchTermForCapturedByMultipleKeywords || ""}
            disableClearable={!selectedSearchTermForCapturedByMultipleKeywords} //✕ only when there is a value
            onInputChange={(_, newVal) => {
              //Every keystroke → Redux → shows X button
              dispatch(
                setSelectedSearchTermForCapturedByMultipleKeywords(
                  newVal ?? "",
                ),
              );
            }}
            onChange={(_, newValue: string | null) => {
              const val = newValue ?? "";
              dispatch(setSelectedSearchTermForCapturedByMultipleKeywords(val));
              setSearchParams((prevParams) => {
                const updatedParams = Object.fromEntries(prevParams);
                if (newValue) {
                  updatedParams.search = newValue;
                  updatedParams.page = "0";
                } else {
                  delete updatedParams.search;
                }
                return updatedParams;
              });
            }}
            // Remove onInputChange - it fires on every keystroke
            getOptionLabel={(opt) => opt || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Term"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ marginLeft: "12px" }} />
                    </InputAdornment>
                  ),
                }}
                color="secondary"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault(); // Prevent default form submission
                    const inputValue = (
                      e.target as HTMLInputElement
                    ).value.trim();

                    if (inputValue) {
                      // Dispatch the search term
                      dispatch(
                        setSelectedSearchTermForCapturedByMultipleKeywords(
                          inputValue,
                        ),
                      );

                      // Update URL params
                      setSearchParams((prevParams) => {
                        const updatedParams = Object.fromEntries(prevParams);
                        updatedParams.search = inputValue;
                        updatedParams.page = "0";
                        return updatedParams;
                      });

                      // Close the dropdown by blurring the input
                      (e.target as HTMLInputElement).blur();
                    }
                  }
                }}
              />
            )}
            size="small"
          />

          <Stack
            flexDirection={"row"}
            justifyContent={"space-between"}
            sx={{ width: "100%" }}
          >
            <CapturedMultipleKeywordFilterItems
              clearSelectedSearchTerm={clearSelectedSearchTerm}
            />
            {/* --- Bulk Actions Header --- */}
            <Stack gap={0.5}>
              {/* Row for Set As icons */}
              <Stack
                flexDirection="row"
                gap={1}
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  {/* Label */}
                  <Typography variant="body1">Set As:</Typography>

                  {icons.map(
                    ({ label, src, coloredSrc, alt, tooltip, dataTracker }) => {
                      const isDisabled =
                        !hasSelection || !hasCreateSearchTermTabPermission;

                      const iconSrc = hasSelection ? coloredSrc : src;

                      return (
                        <span key={label}>
                          <Tooltip
                            title={!isDisabled ? tooltip : ""}
                            arrow
                            disableHoverListener={isDisabled}
                          >
                            <span>
                              <IconButton
                                data-tracker={dataTracker}
                                size="small"
                                disabled={isDisabled}
                                onClick={() => handleClick(label)}
                              >
                                <img
                                  src={iconSrc}
                                  alt={alt}
                                  width={hasSelection ? 24 : 32}
                                  height={hasSelection ? 24 : 32}
                                />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </span>
                      );
                    },
                  )}
                </Stack>
              </Stack>

              {/* Message BELOW */}
              <Typography variant="body2" color="text.secondary">
                * To perform bulk actions, select at least one row.
              </Typography>
            </Stack>
          </Stack>

          {/* Dialog for Other Keywords */}
          <OtherKeywordForCaptureByMultiple
            open={isOpenOtherKeywordDialogForCapturedByMultipleKeywords}
            onClose={handleCloseTheOtherKeywordDialog}
          />
          <DefinitionModalForCaptureByKeywords
            open={isDefinitionModalOpenForCapturedByMultipleKeywords}
            onClose={handleCloseDefinitionModal}
          />
        </Stack>
      </Paper>
    </>
  );
}
export default CapturedMultipleKeywordFilterCard;
